import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';

// Components
import CompanyEditLogo from './CompanyEditLogo';
import SectionHeader from '../../../_molecules/SectionHeader';
import Asterisk from '@atoms/Asterisk';
import Radios from '@atoms/Radios';
import SubmitButton from '@atoms/SubmitButton';
import Textarea from '@atoms/Textarea';
import TextInput from '@atoms/TextInput';
import BusinessTypeSelection from '@molecules/BusinessTypeSelection';
import LoadingArea from '@molecules/LoadingArea';
import SelectCountries from '@molecules/SelectCountries';
import SelectStates from '@molecules/SelectStates';
import {H2, H4, BodyMedium, BodySmall, LabelLarge, LinkSmall, LabelSmall, P, H3, DividerLightGreyStyled} from '@styled';

// Functionality
import { mediaQueries } from '@general/breakpoints';
import { convertMemberTypeTextToCode } from '@general/helpers/member.helpers';
import { mapGraphCompanyToForm } from '@general/transforms/company.transform';
import useCompanyUpdate from '@hooks/useCompanyUpdate';
import useViewer from '@hooks/useViewer';
import useDocumentTitle from '@hooks/useDocumentTitle';
import SectionBreadcrumbs from "../../../_molecules/SectionBreadcrumbs";
import SectionBreadcrumb from "../../../_molecules/SectionBreadcrumb";
import EditProfileNavLink from "@molecules/EditProfileNavLink";
import classNames from "classnames";
import CompanyEditPage from "./CompanyEditPage";

const FormContainer = styled.div`
    @media ${mediaQueries.lg} {
        width: 50%;
    }
`;

const FormLabel = styled.label`
    margin-bottom: 0;
    width: 100%;
`;

const FormLabelText = styled(LabelLarge)`
    display: block;
    margin-bottom: 8px;
`;

const LinkMedium = styled(LinkSmall)`
    font-size: 18px;
`;

const BusinessTypesPrimaryLabel = () => <H4 className="mb-0.5rem">Primary Business Category</H4>;
const BusinessTypesSecondaryLabel = () => <H4 className="mt-2.5rem mb-0.5rem">Secondary Business Category</H4>;

const CompanyEditForm = ({ pageTitleSub, prevNavLink, prevNavLabel, nextNavLink, nextNavLabel }) => {
    const { viewer } = useViewer();
    const { documentTitle } = useDocumentTitle(pageTitleSub);
    const [formValues, setFormValues] = useState();

    const { updateCompany, isSubmitting, isSuccessful, error } = useCompanyUpdate();

    useEffect(() => {
        if (!viewer) {
            return;
        }

        setFormValues({
            ...mapGraphCompanyToForm(viewer.company),
            hasSecondaryTypes: false,
        });
    }, [viewer]);

    const submitForm = values => {
        const { __typename, id, recordId, minorityOwned, womanOwned, ...rest } = values;

        updateCompany({
            variables: {
                ...rest,
                minorityOwned: minorityOwned === '1',
                womanOwned: womanOwned === '1',
            },
        });
    };

    const navClasses = classNames({
        'd-flex': true,
        'justify-content-between': prevNavLink && nextNavLink,
        'justify-content-end': !prevNavLink && nextNavLink,
    });

    if (!formValues) {
        return <LoadingArea />;
    }

    return (
        <div>
            <SectionHeader title="My Account" />

            <div className="flex justify-content-between mt-2">
                <SectionBreadcrumbs>
                    <SectionBreadcrumb route="/my-icsc/account">My Account</SectionBreadcrumb>
                    <SectionBreadcrumb isActive>{documentTitle}</SectionBreadcrumb>
                </SectionBreadcrumbs>
            </div>

            <H3 className="mt-3 mb-4">{documentTitle}</H3>

            <div className="mb-2.5rem mt-2rem">
                <H2>Hi {viewer.firstName},</H2>
                <BodyMedium>
                    As the official member of {formValues.name}, you have permission to edit the company's profile page.
                    To edit your company's name, please email{' '}
                    <LinkMedium href="mailto:membership@icsc.com?subject=My ICSC Profile">
                        membership@icsc.com
                    </LinkMedium>{' '}
                    a written confirmation of the new company name.
                </BodyMedium>
            </div>

            <FormContainer>
                <Formik
                    enableReinitialize
                    initialValues={formValues}
                    validationSchema={Yup.object({
                        mainAddress: Yup.object().shape({
                            country: Yup.string().required('Country is required'),
                            address: Yup.string().required('Address 1 is required'),
                            city: Yup.string().required('City is required'),
                            stateProvince: Yup.string().required('State/Province is required'),
                        }),
                        primaryBusinessType: Yup.string.apply().required('Primary Business Category is required'),
                        secondaryBusinessTypes: Yup.array().when('hasSecondaryTypes', {
                            is: true,
                            then: Yup.array().required('At least one Secondary Business Category is required'),
                        }),
                    })}
                    onSubmit={submitForm}
                >
                    {({ values }) => (
                        <Form noValidate>
                            <fieldset className="mb-2.5rem">
                                <H4 className="mb-1rem">Company Logo</H4>
                                <CompanyEditLogo />
                            </fieldset>

                            <fieldset className="mb-2.5rem">
                                <H4 className="mb-0.5rem">Company Biography</H4>
                                <FormLabel htmlFor="description" className="mb-0">
                                    <Textarea name="description" label="Tell us about your company" rows="3" />
                                </FormLabel>

                                <BodySmall className="text-dark-gray">600 characters or less.</BodySmall>
                            </fieldset>

                            <fieldset className="mb-2.5rem">
                                <H4 className="mb-1rem">Company Contact Information</H4>
                                <FormLabel htmlFor="website">
                                    <FormLabelText>Website</FormLabelText>
                                    <TextInput name="website" placeholder="Website" />
                                </FormLabel>

                                <FormLabel htmlFor="phone">
                                    <FormLabelText>Phone</FormLabelText>
                                    <TextInput name="phone" placeholder="Phone" />
                                </FormLabel>

                                <FormLabel htmlFor="linkedinUrl">
                                    <FormLabelText>LinkedIn URL</FormLabelText>
                                    <TextInput name="linkedinUrl" placeholder="LinkedIn URL" />
                                </FormLabel>

                                <FormLabel htmlFor="twitterUrl">
                                    <FormLabelText>Twitter URL</FormLabelText>
                                    <TextInput name="twitterUrl" placeholder="Twitter URL" />
                                </FormLabel>

                                <SelectCountries name="mainAddress[country]" label="Select Your Country" required />

                                <FormLabel htmlFor="mainAddress[address]">
                                    <FormLabelText>
                                        Address 1 <Asterisk />
                                    </FormLabelText>

                                    <TextInput type="text" name="mainAddress[address]" required />
                                </FormLabel>

                                <FormLabel htmlFor="mainAddress[address2]">
                                    <FormLabelText>Address 2</FormLabelText>
                                    <TextInput type="text" name="mainAddress[address2]" />
                                </FormLabel>

                                <FormLabel htmlFor="mainAddress[address3]">
                                    <FormLabelText>Address 3</FormLabelText>
                                    <TextInput type="text" name="mainAddress[address3]" />
                                </FormLabel>

                                <FormLabel htmlFor="mainAddress[city]">
                                    <FormLabelText>
                                        City <Asterisk />
                                    </FormLabelText>
                                    <TextInput type="text" name="mainAddress[city]" required />
                                </FormLabel>

                                <SelectStates
                                    name="mainAddress[stateProvince]"
                                    countryFieldName="mainAddress[country]"
                                    required
                                />

                                <FormLabel htmlFor="mainAddress[zip]">
                                    <FormLabelText>Zip / Mail Code</FormLabelText>
                                    <TextInput type="text" name="mainAddress[zip]" />
                                </FormLabel>
                            </fieldset>

                            <fieldset className="mb-2.5rem">
                                <div>
                                    <BusinessTypeSelection
                                        memberType={convertMemberTypeTextToCode(viewer?.memberType)}
                                        primaryBusinessType={values.primaryBusinessType}
                                        primaryBusinessTypeFieldName="primaryBusinessType"
                                        secondaryBusinessType={values.secondaryBusinessType}
                                        secondaryBusinessTypeFieldName="secondaryBusinessTypes"
                                        PrimaryLabel={BusinessTypesPrimaryLabel}
                                        SecondaryLabel={BusinessTypesSecondaryLabel}
                                    />
                                </div>
                            </fieldset>

                            <fieldset className="mb-2.5rem">
                                <H4 className="mb-1rem">Other</H4>

                                <P>
                                    <LabelSmall>
                                        ICSC is committed to advancing social and economic inclusion by exposing
                                        minority and women owned businesses (MWOBs) to a broader audience of potential
                                        partners/customers. Through our new initiative, you may apply to have your
                                        business identified as MWOB in our membership directory- making it easier for
                                        larger corporations who are committed to working with underrepresented
                                        businesses to find you.
                                    </LabelSmall>
                                </P>

                                <P>
                                    <LabelSmall>
                                        To receive the MWOB tag in our database, your business must be certified by a
                                        third-party as at least 51% owned, operated and managed by an individual that is
                                        an ethnic minority or woman. The process should include annual
                                        re-certifications. Any agency that maintains these standards will be accepted as
                                        a viable certification source. Please submit proof of certification to{' '}
                                        <LinkSmall className="text-sm" href="mailto:mwobinitiative@icsc.com">
                                            mwobinitiative@icsc.com.
                                        </LinkSmall>
                                        .
                                    </LabelSmall>
                                </P>

                                <P>
                                    <LabelSmall>
                                        If you have any questions, please contact Jazmen Johnson at{' '}
                                        <LinkSmall className="text-sm" href="mailto:jjohnson@icsc.com">
                                            jjohnson@icsc.com
                                        </LinkSmall>
                                        .
                                    </LabelSmall>
                                </P>

                                <div>
                                    <FormLabelText>Is your company minority-owned?</FormLabelText>
                                    <Radios
                                        name="minorityOwned"
                                        options={[
                                            {
                                                value: '1',
                                                label: 'Yes',
                                            },
                                            {
                                                value: '0',
                                                label: 'No',
                                            },
                                        ]}
                                    />
                                </div>

                                <div>
                                    <FormLabelText>Is your company woman-owned?</FormLabelText>
                                    <Radios
                                        name="womanOwned"
                                        options={[
                                            {
                                                value: '1',
                                                label: 'Yes',
                                            },
                                            {
                                                value: '0',
                                                label: 'No',
                                            },
                                        ]}
                                    />
                                </div>
                            </fieldset>

                            <div className="mt-1rem">
                                <SubmitButton
                                    type="submit"
                                    buttonClasses="editproperty mt-0"
                                    isSubmitting={isSubmitting}
                                    isSuccessful={isSuccessful}
                                    isError={!!error}
                                    submittingText="Saving Company Profile"
                                    errorText="Failed to Save Company Profile"
                                    successText="Company Profile Saved"
                                >
                                    Save Your Company Profile
                                </SubmitButton>
                            </div>
                        </Form>
                    )}
                </Formik>
            </FormContainer>
            <DividerLightGreyStyled className="mt-5 mb-4"/>
            <div className={navClasses}>
                {prevNavLink && (
                    <EditProfileNavLink type="prev" to={prevNavLink}>
                        {prevNavLabel}
                    </EditProfileNavLink>
                )}
                {nextNavLink && (
                    <EditProfileNavLink type="next" to={nextNavLink}>
                        {nextNavLabel}
                    </EditProfileNavLink>
                )}
            </div>
        </div>
    );
};

CompanyEditForm.propTypes = {
    pageTitleSub: PropTypes.string,
    prevNavLink: PropTypes.string,
    prevNavLabel: PropTypes.string,
    nextNavLink: PropTypes.string,
    nextNavLabel: PropTypes.string,
};

export default CompanyEditForm;
